/**
 * CompareDatesView — split-screen overlay показывает 2 даты side-by-side.
 *
 * MVP-implementation: НЕ создаёт второй MapLibre instance (дорого, race
 * conditions). Вместо этого рендерит мини-bar с кнопками выбора двух дат
 * и slider для свайпа между ними. Когда compare включён, дополнительно
 * показывает "tape divider" с двумя метками и переключение через клик.
 *
 * Полная split-view (2 maps синхронизированных) — Phase 4.
 */
import { ArrowLeftRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMapStore } from '@/lib/map/useMapStore';

interface CompareDatesViewProps {
  /** Доступные даты для выбора. */
  availableDates: string[];
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
}

export function CompareDatesView({ availableDates }: CompareDatesViewProps) {
  const compareMode = useMapStore((s) => s.compareMode);
  const setCompareMode = useMapStore((s) => s.setCompareMode);
  const setSelectedDate = useMapStore((s) => s.setSelectedDate);
  const selectedDate = useMapStore((s) => s.selectedDate);

  if (!compareMode.enabled) return null;

  const showA = () => {
    if (compareMode.dateA) setSelectedDate(compareMode.dateA);
  };
  const showB = () => {
    if (compareMode.dateB) setSelectedDate(compareMode.dateB);
  };

  const close = () => {
    setCompareMode({ enabled: false, dateA: null, dateB: null });
  };

  const setA = (iso: string) =>
    setCompareMode({ ...compareMode, dateA: iso });
  const setB = (iso: string) =>
    setCompareMode({ ...compareMode, dateB: iso });

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 rounded-xl bg-card/95 backdrop-blur border border-border shadow-lg px-3 py-2">
      <div className="flex items-center gap-3">
        <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-foreground">
          Сравнение дат:
        </span>

        {/* Date A select */}
        <select
          value={compareMode.dateA ?? ''}
          onChange={(e) => setA(e.target.value)}
          className="text-xs bg-background border border-border rounded px-2 py-1"
        >
          <option value="">—</option>
          {availableDates.map((d) => (
            <option key={d} value={d}>
              {fmt(d)}
            </option>
          ))}
        </select>

        <Button
          type="button"
          variant={selectedDate === compareMode.dateA ? 'default' : 'outline'}
          size="sm"
          onClick={showA}
          disabled={!compareMode.dateA}
        >
          Показать A
        </Button>

        <span className="text-muted-foreground">↔</span>

        <Button
          type="button"
          variant={selectedDate === compareMode.dateB ? 'default' : 'outline'}
          size="sm"
          onClick={showB}
          disabled={!compareMode.dateB}
        >
          Показать B
        </Button>

        {/* Date B select */}
        <select
          value={compareMode.dateB ?? ''}
          onChange={(e) => setB(e.target.value)}
          className="text-xs bg-background border border-border rounded px-2 py-1"
        >
          <option value="">—</option>
          {availableDates.map((d) => (
            <option key={d} value={d}>
              {fmt(d)}
            </option>
          ))}
        </select>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={close}
          aria-label="Закрыть сравнение"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
